import * as React from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { LocationCombobox } from '@/components/po/LocationCombobox'
import { ProductSearchPicker } from '@/components/po/ProductSearchPicker'
import { VendorCombobox } from '@/components/po/VendorCombobox'
import { useAllPOs, useLocations } from '@/hooks/usePOs'
import {
  useAddToPO,
  type AddToPOVariantPayload,
  type ProductRequest,
} from '@/hooks/useProductRequests'
import type { POLineItemFormValues } from '@/lib/schemas/po'
import { cn } from '@/lib/utils'

const selectCls =
  'h-8 w-full border border-[var(--line-input)] bg-[var(--surface-card)] px-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--brand)]'

export function AddToPOModal({
  request,
  open,
  onOpenChange,
}: {
  request: ProductRequest | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { data: pos = [] } = useAllPOs()
  const { data: locations = [] } = useLocations()
  const addToPO = useAddToPO()
  const busy = addToPO.isPending

  // Only a DRAFT PO can take new lines — the backend 409s on anything else,
  // so don't offer it in the first place.
  const drafts = React.useMemo(() => pos.filter((p) => p.status === 'DRAFT'), [pos])

  const manual = request != null && request.variant_id == null

  const [picked, setPicked] = React.useState<POLineItemFormValues | null>(null)
  const [target, setTarget] = React.useState<'existing' | 'new'>('existing')
  const [poId, setPoId] = React.useState('')
  const [vendor, setVendor] = React.useState('')
  const [location, setLocation] = React.useState('')
  const [qty, setQty] = React.useState('1')
  // Once the user has touched the vendor themselves, nothing may overwrite it —
  // a later variant pick prefills only into an untouched field.
  const vendorDirty = React.useRef(false)

  React.useEffect(() => {
    if (!open || !request) return
    setPicked(null)
    // No drafts to pick from → the only sensible target is a new one.
    setTarget(drafts.length > 0 ? 'existing' : 'new')
    setPoId('')
    // The product's own Shopify vendor is who we'd actually buy this from;
    // `vendor` is the hand-typed sourcing note and only stands in when there
    // is no linked product to read it off.
    setVendor(request.product_vendor ?? request.vendor ?? '')
    vendorDirty.current = false
    setLocation('')
    setQty(String(request.qty ?? 1))
    addToPO.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, request?.id])

  function handleVendorChange(v: string) {
    vendorDirty.current = true
    setVendor(v)
  }

  /** A manual request gets its vendor from the variant the user just picked —
   *  that pick is the first time we know who actually sells this. */
  function handlePick(item: POLineItemFormValues) {
    setPicked(item)
    if (!vendorDirty.current && item.vendor) setVendor(item.vendor)
  }

  if (!request) return null

  async function handleConfirm() {
    if (!request) return
    const qtyNum = Math.trunc(Number(qty))
    if (!Number.isInteger(qtyNum) || qtyNum < 1) {
      toast.error('Qty must be 1 or more')
      return
    }
    if (manual && !picked) {
      toast.error('Pick the Shopify variant to order')
      return
    }
    if (target === 'existing' && !poId) {
      toast.error('Pick a draft PO')
      return
    }
    if (target === 'new' && !vendor.trim()) {
      toast.error('A new PO needs a vendor')
      return
    }

    let variant: AddToPOVariantPayload | undefined
    if (picked) {
      variant = {
        variant_id: picked.variant_id,
        product_id: picked.product_id,
        product_title: picked.product_title,
        variant_title: picked.variant_title || undefined,
        sku: picked.sku || undefined,
        barcode: picked.barcode || undefined,
        vendor: picked.vendor || undefined,
        // Zeroes are "the picker had nothing", not a real price — omit them so
        // the backend falls back to the request's approx_price / est_cost.
        retail_price: picked.retail_price || undefined,
        cost_base: picked.cost_base || undefined,
      }
    }

    try {
      const res = await addToPO.mutateAsync({
        id: request.id,
        body: {
          qty: qtyNum,
          variant,
          ...(target === 'existing'
            ? { po_id: poId }
            : {
                new_po: {
                  vendor: vendor.trim(),
                  location_to_receive: location.trim() || undefined,
                },
              }),
        },
      })
      toast.success(`Added to ${res.po_id} — ${res.qty_on_line} on that line`)
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add to PO')
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)] sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="text-base">Add to purchase order</DialogTitle>
          <DialogDescription className="text-xs text-[var(--mute)]">
            {request.customer_name} · {request.product_title || '(no title)'} · qty{' '}
            {request.qty}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {manual && (
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-[var(--text-primary)]">
                Pick the Shopify variant to order
              </span>
              {picked ? (
                <div className="flex items-center gap-2 border border-[var(--line-warm)] bg-[var(--surface-page)] px-3 py-2 text-xs">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-[var(--text-primary)]">
                      {picked.product_title}
                    </div>
                    <div className="text-[var(--mute)]">
                      {picked.variant_title || '—'}
                      {picked.sku ? ` · SKU ${picked.sku}` : ''}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={busy}
                    onClick={() => setPicked(null)}
                  >
                    Change
                  </Button>
                </div>
              ) : (
                <ProductSearchPicker disabled={busy} onPick={handlePick} />
              )}
              <span className="text-[11px] text-[var(--mute)]">
                This request was typed by hand — there is nothing orderable until a
                variant is chosen.
              </span>
            </div>
          )}

          {/* Target PO */}
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-[var(--text-primary)]">Target PO</span>
            <div className="flex gap-4 text-xs">
              <label className={cn('flex items-center gap-1.5', drafts.length === 0 && 'opacity-50')}>
                <input
                  type="radio"
                  name="add-to-po-target"
                  checked={target === 'existing'}
                  disabled={busy || drafts.length === 0}
                  onChange={() => setTarget('existing')}
                />
                Existing draft
              </label>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="add-to-po-target"
                  checked={target === 'new'}
                  disabled={busy}
                  onChange={() => setTarget('new')}
                />
                New draft PO
              </label>
            </div>

            {target === 'existing' ? (
              <select
                className={selectCls}
                value={poId}
                disabled={busy || drafts.length === 0}
                onChange={(e) => setPoId(e.target.value)}
                aria-label="Draft purchase order"
              >
                <option value="">
                  {drafts.length === 0 ? '— no draft POs —' : '— select draft PO —'}
                </option>
                {drafts.map((p) => (
                  <option key={p.po_id} value={p.po_id}>
                    {p.po_number || p.po_id} · {p.vendor || p.supplier || 'no vendor'}
                  </option>
                ))}
              </select>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <span className="text-[11px] text-[var(--mute)]">Vendor</span>
                  <VendorCombobox
                    value={vendor}
                    onChange={handleVendorChange}
                    disabled={busy}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[11px] text-[var(--mute)]">Receive at</span>
                  <LocationCombobox
                    value={location}
                    locations={locations}
                    onChange={setLocation}
                    disabled={busy}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex w-28 flex-col gap-1">
            <span className="text-[11px] text-[var(--mute)]">Qty</span>
            <Input
              type="number"
              min={1}
              value={qty}
              disabled={busy}
              onChange={(e) => setQty(e.target.value)}
              className="h-8 text-xs"
              data-testid="add-to-po-qty"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            disabled={busy}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-7 text-xs"
            disabled={busy}
            onClick={() => void handleConfirm()}
            data-testid="add-to-po-confirm"
          >
            {busy ? 'Adding…' : 'Add to PO'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
